/* eslint-disable react/jsx-indent, react/jsx-closing-tag-location */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  InputAdornment,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'

import { BillingChargesBreakdownPie } from './billingChargesBreakdownPie'
import { BillingChargesInGraphTable } from './billingChargesInGraphTable'
import {
  BREAKDOWN_BY,
  chargeRowIncludedInGraph,
  compareRows,
  hueFromString,
  pieSlicePercentLabels,
  rowMatchesSearch,
  sortKeyForColumn
} from './chargesPanelUtils'
import { formatMonthKeyShortYear } from '../../utils/dashboardMath'

const DataTranspositionChargesPanel = ({
  qboBillingRows = [],
  allChargeOptions = [],
  chargesPresentInData,
  selectedCharges = {},
  excludedChargeRowKeys = {},
  clientSettings = {},
  onToggleRowInGraph,
  onSelectAllVisible
}) => {
  const theme = useTheme()
  const bill = theme.custom.colors.billingDrawer
  const accent = bill.accent

  const [search, setSearch] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortColumn, setSortColumn] = useState('amount')
  const [breakdownMode, setBreakdownMode] = useState(BREAKDOWN_BY.CHARGE_TYPE)

  const requestSort = (property) => {
    const isAsc = sortColumn === property && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortColumn(property)
  }

  const filteredRows = useMemo(
    () => (Array.isArray(qboBillingRows) ? qboBillingRows.filter((r) => rowMatchesSearch(r, search)) : []),
    [qboBillingRows, search]
  )

  const rowsInGraphTotals = useMemo(
    () =>
      filteredRows.filter((row) =>
        chargeRowIncludedInGraph(row, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys)
      ),
    [filteredRows, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys]
  )

  const pieChartData = useMemo(() => {
    if (!rowsInGraphTotals.length) return null

    const totalsByKey = new Map()
    const add = (key, amt) => {
      const k = key || '—'
      totalsByKey.set(k, (totalsByKey.get(k) || 0) + amt)
    }

    rowsInGraphTotals.forEach((row) => {
      const amt = Number(row?.amount) || 0
      if (amt <= 0) return
      if (breakdownMode === BREAKDOWN_BY.CHARGE_TYPE) {
        add(row.chargeCategory || 'Unknown', amt)
      } else if (breakdownMode === BREAKDOWN_BY.CLIENT) {
        add(row.clientName || '—', amt)
      } else {
        add(row.billingMonthKey || '', amt)
      }
    })

    let entries = [...totalsByKey.entries()].filter(([, v]) => v > 0)
    if (!entries.length) return null

    if (breakdownMode === BREAKDOWN_BY.MONTH) {
      entries = entries.sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    } else {
      entries = entries.sort((a, b) => b[1] - a[1])
    }

    const grandTotal = entries.reduce((s, [, v]) => s + v, 0)
    if (grandTotal <= 0) return null

    const labels = entries.map(([k]) =>
      breakdownMode === BREAKDOWN_BY.MONTH ? (k ? formatMonthKeyShortYear(k) : '—') : k
    )

    const backgroundColor = entries.map(([key], i) => {
      if (breakdownMode === BREAKDOWN_BY.CLIENT) {
        const c = clientSettings[key]?.color
        if (c) return alpha(c, 0.85)
      }
      const h = (hueFromString(key) + i * 17) % 360
      return `hsl(${h} 55% 48%)`
    })

    return {
      labels,
      datasets: [
        {
          data: entries.map(([, v]) => v),
          backgroundColor,
          borderWidth: 2,
          borderColor: alpha(theme.palette.background.paper, 0.95)
        }
      ]
    }
  }, [rowsInGraphTotals, breakdownMode, clientSettings, theme.palette.background.paper])

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 10,
            font: { size: 11 },
            color: bill.text
          }
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const value = Number(ctx.raw) || 0
              const sum = ctx.dataset.data.reduce((a, b) => a + b, 0)
              const pct = sum > 0 ? (value / sum) * 100 : 0
              return `${ctx.label}: ${value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} (${pct.toFixed(1)}%)`
            }
          }
        }
      }
    }),
    [bill.text]
  )

  const pieLabelPlugins = useMemo(() => [pieSlicePercentLabels(bill.text)], [bill.text])

  const sortedRows = useMemo(() => {
    const getVal = (row) => sortKeyForColumn(row, sortColumn)
    return [...filteredRows].sort(compareRows(sortOrder, getVal))
  }, [filteredRows, sortOrder, sortColumn])

  const allVisibleInGraph = useMemo(() => {
    if (!sortedRows.length) return false
    return sortedRows.every((row) =>
      chargeRowIncludedInGraph(row, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys)
    )
  }, [sortedRows, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys])

  const someVisibleInGraph = useMemo(
    () =>
      sortedRows.some((row) =>
        chargeRowIncludedInGraph(row, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys)
      ),
    [sortedRows, allChargeOptions, chargesPresentInData, selectedCharges, excludedChargeRowKeys]
  )

  const headCellSx = {
    fontWeight: 800,
    fontSize: '0.7rem',
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: alpha(accent, 0.95),
    borderColor: alpha(bill.border, 0.85),
    py: 1,
    px: 1,
    whiteSpace: 'nowrap',
    backgroundColor: alpha(bill.surface, 0.95)
  }

  const sortLabelSx = {
    '&.Mui-active': { color: `${accent} !important` },
    '& .MuiTableSortLabel-icon': { color: `${alpha(accent, 0.85)} !important` }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: 0, flex: 1, width: '100%' }}>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${bill.border}`,
          borderLeft: `4px solid ${accent}`,
          backgroundColor: alpha(bill.surface, 0.85),
          boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        <Box
          component='header'
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 1.25, md: 2 },
            px: { xs: 1.25, sm: 1.75 },
            py: 1.25,
            backgroundColor: alpha(accent, 0.1),
            borderBottom: `1px solid ${alpha(accent, 0.22)}`
          }}
        >
          <Typography
            component='div'
            variant='h6'
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.05rem', sm: '1.2rem' },
              lineHeight: 1.25,
              color: bill.text,
              flex: { xs: 'none', md: '0 0 auto' },
              minWidth: 0
            }}
          >
            Charges in this graph
          </Typography>
          <TextField
            size='small'
            fullWidth
            placeholder='Search…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: bill.muted, fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              flex: { xs: 'none', md: '1 1 auto' },
              maxWidth: { xs: '100%', md: 360 },
              ml: { xs: 0, md: 'auto' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: alpha(bill.surface, 0.72),
                borderRadius: 1.5,
                '& fieldset': { borderColor: alpha(accent, 0.35) },
                '&:hover fieldset': { borderColor: alpha(accent, 0.5) },
                '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.75) }
              }
            }}
          />
        </Box>

        <CardContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: alpha('#000', 0.15),
            '&:last-child': { pb: 0 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flex: 1,
              minHeight: { xs: 'auto', md: 320 },
              alignItems: 'stretch'
            }}
          >
            <BillingChargesInGraphTable
              bill={bill}
              accent={accent}
              sortedRows={sortedRows}
              sortOrder={sortOrder}
              sortColumn={sortColumn}
              onRequestSort={requestSort}
              allRowsSelectedForGraph={allVisibleInGraph}
              someRowsSelectedForGraph={someVisibleInGraph}
              onToggleSelectAllVisible={onSelectAllVisible}
              selectedCharges={selectedCharges}
              excludedChargeRowKeys={excludedChargeRowKeys}
              chargesPresentInData={chargesPresentInData}
              allChargeOptions={allChargeOptions}
              clientSettings={clientSettings}
              onToggleRowInGraph={onToggleRowInGraph}
              headCellSx={headCellSx}
              sortLabelSx={sortLabelSx}
            />

            <BillingChargesBreakdownPie
              bill={bill}
              accent={accent}
              breakdownMode={breakdownMode}
              onBreakdownModeChange={setBreakdownMode}
              chartData={pieChartData}
              chartOptions={pieOptions}
              labelPlugins={pieLabelPlugins}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export { DataTranspositionChargesPanel }
